import { mergePoints, type MergeResult } from "./join-merge.ts";
import { splitSegment } from "./route-graph.ts";
import type { ScreenHitGrid, ScreenPoint } from "./screen-hit.ts";
import type { LngLat } from "./types.ts";

/** Screen distance at which a dragged or placed point catches another point or line. */
export const VERTEX_SNAP_PX = 12;

export type SnapTarget =
  | { kind: "vertex"; index: number; x: number; y: number }
  | { kind: "segment"; segment: number; x: number; y: number; t: number };

/** Segments that touch `index`, so a dragged point never catches its own lines. */
export function incidentMask(segments: ReadonlyArray<readonly [number, number]>, index: number | null): Uint8Array {
  const mask = new Uint8Array(segments.length);
  if (index == null) return mask;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg && (seg[0] === index || seg[1] === index)) mask[i] = 1;
  }
  return mask;
}

/**
 * Nearest point within `radius` px of `at`, else the nearest line. A point
 * always wins over a line, so ends join instead of landing beside each other.
 * `exclude` is the point being dragged; its own lines are skipped too.
 */
export function pickSnapTarget(input: {
  grid: ScreenHitGrid;
  points: readonly ScreenPoint[];
  segments: ReadonlyArray<readonly [number, number]>;
  at: ScreenPoint;
  radius?: number;
  exclude?: number | null;
  /** Precomputed {@link incidentMask} for `exclude`. */
  hidden?: Uint8Array;
  segmentsToo?: boolean;
}): SnapTarget | null {
  const radius = input.radius ?? VERTEX_SNAP_PX;
  const exclude = input.exclude ?? null;
  const { x, y } = input.at;
  let best: number | null = null;
  let bestD = Infinity;
  for (const index of input.grid.pointsNear(x, y, radius)) {
    if (index === exclude) continue;
    const p = input.points[index];
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    const d = Math.hypot(p.x - x, p.y - y);
    if (d > radius) continue;
    if (d < bestD || (d === bestD && best != null && index < best)) {
      bestD = d;
      best = index;
    }
  }
  if (best != null) {
    const p = input.points[best]!;
    return { kind: "vertex", index: best, x: p.x, y: p.y };
  }
  if (input.segmentsToo === false) return null;
  const hidden = input.hidden ?? incidentMask(input.segments, exclude);
  let line: SnapTarget | null = null;
  bestD = Infinity;
  for (const index of input.grid.segmentsNear(x, y, radius, hidden)) {
    const seg = input.segments[index];
    if (!seg) continue;
    const a = input.points[seg[0]];
    const b = input.points[seg[1]];
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) continue;
    const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / len2));
    const px = a.x + t * dx;
    const py = a.y + t * dy;
    const d = Math.hypot(px - x, py - y);
    if (d > radius || d >= bestD) continue;
    bestD = d;
    line = { kind: "segment", segment: index, x: px, y: py, t };
  }
  return line;
}

export type DropTarget =
  | { kind: "vertex"; index: number }
  | { kind: "segment"; segment: number; at: LngLat };

/**
 * Join the dropped point `dragged` into a target. A point target merges at the
 * target's position. A line target is split at `at` and the new middle point
 * merges with `dragged`. `pointMap` is indexed by the input points.
 */
export function joinDroppedPoint(
  points: readonly LngLat[],
  segments: ReadonlyArray<readonly [number, number]>,
  start: number | null,
  dragged: number,
  target: DropTarget,
): MergeResult | null {
  if (!points[dragged]) return null;
  if (target.kind === "vertex") {
    const at = points[target.index];
    if (!at || target.index === dragged) return null;
    return mergePoints(points, segments, start, [dragged, target.index], at);
  }
  const seg = segments[target.segment];
  if (!seg || seg[0] === dragged || seg[1] === dragged) return null;
  const split = splitSegment(points, segments, target.segment, target.at);
  if (!split) return null;
  const at = split.points[split.index];
  if (!at) return null;
  const merged = mergePoints(split.points, split.segments, start, [dragged, split.index], at);
  if (!merged) return null;
  return { ...merged, pointMap: merged.pointMap.slice(0, points.length) };
}
